import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import { useAuth } from '../context/AuthContext';

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateForm = (): boolean => {
    if (!username || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters long');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await signup(username, email, password);
      setSuccess(true); // Show email verification message
    } catch (err: any) {
      setError(err.message || 'Signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: { xs: 2, md: 4 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              mb: 2,
            }}
          >
            <PersonAddOutlinedIcon sx={{ color: 'white', fontSize: { xs: 24, md: 32 } }} />
          </Box>

          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Verify Your Email
          </Typography>

          <Alert severity="success" sx={{ width: '100%', mb: 3, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Account created successfully!
          </Alert>

          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center', fontSize: { xs: '0.813rem', md: '1rem' } }}>
            A verification email has been sent to <strong>{email}</strong>.
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Please check your email and click the verification link to activate your account. The link will expire in 7 days.
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            If you don't see the email, please check your spam folder.
          </Typography>

          <Divider sx={{ width: '100%', my: 2 }} />

          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            <Link
              onClick={() => navigate('/login')}
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                fontWeight: '600',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Back to Login
            </Link>
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: { xs: 2, md: 4 } }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            mb: 2,
          }}
        >
          <PersonAddOutlinedIcon sx={{ color: 'white', fontSize: { xs: 24, md: 32 } }} />
        </Box>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Create Account
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
          Sign up to start using the approval management system
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2, fontSize: { xs: '0.75rem', md: '0.875rem' } }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            placeholder="Min 3 characters"
            disabled={isLoading}
            autoFocus
            sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            placeholder="your@email.com"
            disabled={isLoading}
            sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            placeholder="Min 6 characters"
            disabled={isLoading}
            sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            placeholder="Re-enter password"
            disabled={isLoading}
            sx={{ '& .MuiOutlinedInput-input': { fontSize: { xs: '0.875rem', md: '1rem' } } }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2, fontSize: { xs: '0.813rem', md: '1rem' } }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </Box>

        <Divider sx={{ width: '100%', my: 3 }} />

        <Box sx={{ textAlign: 'center', width: '100%', mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Already have an account?{' '}
            <Link
              onClick={() => navigate('/login')}
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                fontWeight: '600',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>
            By signing up, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Signup;
